import React, { useState, useEffect, useCallback, useMemo } from "react";
import LoanDataService from "../services/LoanService";
import UserDataService from "../services/UserService";
import LoanApplicationFeeService from "../services/LoanApplicationFeeService";
import LoanApplicationFeeModal from "../../components/LoanApplicationFeeModal";
import { useParams, useNavigate } from "react-router-dom";
import {
  Paper,
  Typography,
  Box,
  Grid,
  TextField,
  Stack,
  Button,
  CircularProgress,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  FormControlLabel,
  Switch,
  InputAdornment,
  Autocomplete
} from "@mui/material";
import VerifiedUserRoundedIcon from "@mui/icons-material/VerifiedUserRounded";
import PostAddRoundedIcon from "@mui/icons-material/PostAddRounded";
import CalculateRoundedIcon from "@mui/icons-material/CalculateRounded";
import CallSplitRoundedIcon from "@mui/icons-material/CallSplitRounded";
import SmallCalculator from "../../components/SmallCalculator";
import StampDutyService from "../services/StampDutyService";
import SplitDepositModal from "../../components/SplitDepositModal";
import { fNumber } from "../../utils/formatNumber";

const toDateInput = (d) => {
  const dt = d ? new Date(d) : new Date();
  const yyyy = dt.getFullYear();
  const mm = String(dt.getMonth() + 1).padStart(2, "0");
  const dd = String(dt.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const parseNum = (val) => {
  if (val === null || val === undefined || val === "") return 0;
  const clean = String(val).replace(/,/g, "").trim();
  const n = parseFloat(clean);
  return Number.isFinite(n) ? n : 0;
};

const formatInputWithCommas = (val) => {
  if (val === null || val === undefined || val === "") return "";
  const clean = String(val).replace(/[^0-9.]/g, "");
  const parts = clean.split(".");
  const intPart = parts[0] ? Number(parts[0]).toLocaleString("en-US") : "";
  if (parts.length > 1) {
    return `${intPart}.${parts[1].slice(0, 2)}`;
  }
  return intPart;
};

const formatToCurrencyString = (val) => {
  const n = parseNum(val);
  if (n === 0 && (!val || String(val).trim() === "")) return "";
  return n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const AddLoan = () => {
  const { owner, id } = useParams(); // accept optional :id for edit
  const navigate = useNavigate();

  const initialLoanState = {
    owner: owner || "",
    date: toDateInput(new Date()),
    amount: "",
    creditOrDebit: "",
    description: "",
    tenor: "6",
    createdAt: null,
    createdBy: null,
    updatedAt: null,
    updatedBy: null,
    newBalance: null
  };

  const [Loan, setLoan] = useState(initialLoanState);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [snackOpen, setSnackOpen] = useState(false);
  const [error, setError] = useState("");
  const [ownerDisplay, setOwnerDisplay] = useState(() => (UserDataService.getCachedUserName ? UserDataService.getCachedUserName(owner) : ""));
  const [showCalc, setShowCalc] = useState(false);
  const [applyStampDuty, setApplyStampDuty] = useState(true);
  const [stampDutyAmount, setStampDutyAmount] = useState("50");
  const [waiveReason, setWaiveReason] = useState("");
  const [splitModalOpen, setSplitModalOpen] = useState(false);
  const isEdit = Boolean(id);
  // tenor dialog state
  const [tenorDialogOpen, setTenorDialogOpen] = useState(false);
  const [tenorMonths, setTenorMonths] = useState("");
  const [tenorError, setTenorError] = useState("");
  // guarantor dialog state (new)
  const [guarantorDialogOpen, setGuarantorDialogOpen] = useState(false);
  const [guarantor1, setGuarantor1] = useState("");
  const [guarantor2, setGuarantor2] = useState("");
  const [guarantorError, setGuarantorError] = useState("");
  const [guarantorOptions, setGuarantorOptions] = useState([]);
  const [loadingGuarantors, setLoadingGuarantors] = useState(false);

  // COT charges state (Commission on Turnover)
  const [applyCot, setApplyCot] = useState(true);
  const [cotRate, setCotRate] = useState(0.1); // 0.1% standard default
  const [customCotRate, setCustomCotRate] = useState("");

  // Loan application fee tracking
  const [memberFeeInfo, setMemberFeeInfo] = useState(null);
  const [loadingFee, setLoadingFee] = useState(false);
  const [appFeeModalOpen, setAppFeeModalOpen] = useState(false);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setLoan(prev => ({ ...prev, [name]: value }));
  };

  useEffect(() => {
    if (!isEdit) return;
    setLoading(true);
    LoanDataService.get(id)
      .then(res => {
        let src = res?.data ?? res ?? {};
        if (Array.isArray(src)) src = src.find(item => (item._id || item.id) === id) || src[0] || {};
        // unwrap single-key wrapper
        if (!src || Object.keys(src).length === 1) {
          const key = Object.keys(src)[0];
          const val = key ? src[key] : undefined;
          if (val && (val._id || val.id)) src = val;
        }

        const ownerVal = src?.owner;
        const ownerId = ownerVal && typeof ownerVal === "object"
          ? (ownerVal._id || ownerVal.id || String(ownerVal))
          : ownerVal;
        // set owner display as "username lastname" when backend returned owner object
        if (ownerVal && typeof ownerVal === "object") {
          const fname = ownerVal.firstName || ownerVal.firstname || ownerVal.first_name || ownerVal.name || "";
          const lname = ownerVal.lastName || ownerVal.lastname || ownerVal.last_name || "";
          setOwnerDisplay((lname ? lname + (fname ? " " : "") : "") + fname);
        } else {
          if (!owner) setOwnerDisplay("");
        }

        setLoan(prev => ({
          ...prev,
          owner: ownerId ?? initialLoanState.owner,
          date: src?.date ?? initialLoanState.date,
          amount: (src?.amount != null) ? String(src.amount) : "",
          creditOrDebit: src?.creditOrDebit ?? prev.creditOrDebit,
          description: src?.description ?? prev.description,
          tenor: src?.tenor != null ? String(src.tenor) : prev.tenor,
          createdAt: src?.createdAt ?? prev.createdAt,
          createdBy: src?.createdBy ?? prev.createdBy,
          updatedAt: src?.updatedAt ?? prev.updatedAt,
          updatedBy: src?.updatedBy ?? prev.updatedBy,
          newBalance: src?.newBalance ?? prev.newBalance,
        }));
        if (src?.tenor != null) {
          setTenorMonths(String(src.tenor));
        }
      })
      .catch((e) => {
        console.error("load Loan:", e, e?.response?.data);
        setError(e?.response?.data?.message || e?.message || "Could not load record");
      })
      .finally(() => setLoading(false));
  }, [id]); // eslint-disable-line

  // if an owner id is provided via route (create new loan), fetch username + lastname
  useEffect(() => {
    if (!owner) return;
    if (ownerDisplay) return;
    const cached = UserDataService.getCachedUserName ? UserDataService.getCachedUserName(owner) : "";
    if (cached) {
      setOwnerDisplay(cached);
      return;
    }
    let mounted = true;
    UserDataService.get(owner)
      .then(res => {
        const u = res?.data ?? res ?? {};
        const fname = u.firstName || u.firstname || u.first_name || u.name || "";
        const lname = u.lastName || u.lastname || u.last_name || "";
        const fullName = (lname ? lname + (fname ? " " : "") : "") + fname;
        if (mounted && fullName) setOwnerDisplay(fullName);
      })
      .catch(() => {});
    return () => { mounted = false; };
  }, [owner, ownerDisplay]);

  // Load member application form fee status
  const targetMemberId = Loan.owner || owner;
  const fetchMemberFee = useCallback((mId) => {
    if (!mId) return;
    setLoadingFee(true);
    LoanApplicationFeeService.getMemberFees(mId)
      .then(res => {
        setMemberFeeInfo(res?.data || null);
      })
      .catch(() => setMemberFeeInfo(null))
      .finally(() => setLoadingFee(false));
  }, []);

  useEffect(() => {
    if (targetMemberId) {
      fetchMemberFee(targetMemberId);
    }
  }, [targetMemberId, fetchMemberFee]);

  // Load registered members full names for Guarantor autocomplete list (Financial I, II, III can see all members across society, excluding borrower)
  const borrowerId = Loan.owner || owner;

  useEffect(() => {
    let mounted = true;
    setLoadingGuarantors(true);
    const fetchId = borrowerId || undefined;
    UserDataService.getGuarantorMembers(fetchId)
      .then(res => {
        if (!mounted) return;
        const list = res?.data || res || [];
        if (Array.isArray(list)) {
          const names = list
            .filter(u => {
              // 1. Only ACTIVE members can serve as guarantors
              const rawStatus = (u.approvedOrNotApproved || "").trim().toLowerCase();
              if (rawStatus && rawStatus !== "active") return false;

              // 2. Exclude the borrower taking the loan by MongoDB ID
              const uId = String(u._id || u.id || "");
              if (borrowerId && uId === String(borrowerId)) return false;

              // 3. Exclude the borrower by name if ownerDisplay is present
              if (ownerDisplay) {
                const bNorm = ownerDisplay.trim().toLowerCase();
                const uFname = (u.firstName || u.firstname || u.first_name || "").trim().toLowerCase();
                const uLname = (u.lastName || u.lastname || u.last_name || "").trim().toLowerCase();
                const uFull1 = `${uLname} ${uFname}`.trim();
                const uFull2 = `${uFname} ${uLname}`.trim();
                const uUname = (u.username || u.name || "").trim().toLowerCase();
                if (bNorm && (bNorm === uFull1 || bNorm === uFull2 || bNorm === uUname)) {
                  return false;
                }
              }
              return true;
            })
            .map(u => {
              const fname = (u.firstName || u.firstname || u.first_name || "").trim();
              const lname = (u.lastName || u.lastname || u.last_name || "").trim();
              const full = (lname ? `${lname} ${fname}`.trim() : fname) || (u.username || u.name || "").trim();
              const reg = u.registrationNo || u.membershipId ? ` (${u.registrationNo || u.membershipId})` : "";
              return full ? `${full}${reg}` : "";
            })
            .filter(Boolean);
          const uniqueNames = Array.from(new Set(names)).sort((a, b) => a.localeCompare(b));
          setGuarantorOptions(uniqueNames);
        }
      })
      .catch(err => console.error("Could not load users for guarantors:", err))
      .finally(() => {
        if (mounted) setLoadingGuarantors(false);
      });
    return () => { mounted = false; };
  }, [borrowerId, ownerDisplay]);

  // Strip registration number or extra qualifiers to compare base identity
  const cleanGuarantorName = (str) => (str || "").replace(/\s*\(.*?\)\s*/g, "").trim().toLowerCase();

  // Derive options for Guarantor 1 (excluding selected Guarantor 2 so 1 and 2 cannot be the same)
  const g1Options = useMemo(() => {
    if (!guarantor2 || !guarantor2.trim()) return guarantorOptions;
    const g2Clean = cleanGuarantorName(guarantor2);
    return guarantorOptions.filter(opt => cleanGuarantorName(opt) !== g2Clean);
  }, [guarantorOptions, guarantor2]);

  // Derive options for Guarantor 2 (excluding selected Guarantor 1 so 1 and 2 cannot be the same)
  const g2Options = useMemo(() => {
    if (!guarantor1 || !guarantor1.trim()) return guarantorOptions;
    const g1Clean = cleanGuarantorName(guarantor1);
    return guarantorOptions.filter(opt => cleanGuarantorName(opt) !== g1Clean);
  }, [guarantorOptions, guarantor1]);

  // Derived COT math
  const isApprovedLoan = Loan.creditOrDebit === "credit";
  const principalNum = parseNum(Loan.amount);
  const effectiveCotRate = applyCot ? (customCotRate !== "" ? Number(customCotRate) : Number(cotRate)) : 0;
  const cotAmount = applyCot ? Math.round((principalNum * effectiveCotRate) / 100) : 0;
  const totalLoanWithCot = principalNum + cotAmount;
  const tenorMonthsNum = Number(Loan.tenor || tenorMonths || 6) || 6;
  const monthlyInstallment = tenorMonthsNum > 0 ? Math.round(totalLoanWithCot / tenorMonthsNum) : 0;

  const saveLoan = async () => {
    setError("");
    if (!Loan.amount || principalNum <= 0 || Number.isNaN(principalNum)) {
      setError("Please enter a valid loan amount");
      return;
    }
    if (!Loan.creditOrDebit) {
      setError("Please select transaction type: Approved Loan or Loan Repayment");
      return;
    }
    if (isApprovedLoan) {
      const tNum = parseInt(Loan.tenor || tenorMonths, 10);
      if (!tNum || Number.isNaN(tNum) || tNum <= 0) {
        setError("Please enter or select a valid repayment tenor (in months) for Approved Loan.");
        return;
      }
    }

    setLoading(true);
    try {
      const finalDebtAmount = isApprovedLoan && applyCot ? totalLoanWithCot : principalNum;

      const payload = {
        owner: Loan.owner,
        date: typeof Loan.date === "string" ? Loan.date : toDateInput(Loan.date),
        amount: finalDebtAmount,
        principalAmount: principalNum,
        cotRate: effectiveCotRate,
        cotAmount: cotAmount,
        isCotApplied: Boolean(isApprovedLoan && applyCot),
        applicationFormFee: memberFeeInfo?.totalPaid || 0,
        applicationFormReceipt: memberFeeInfo?.latest?.receiptNo || "",
        creditOrDebit: String(Loan.creditOrDebit),
        description: (Loan.description || "").trim(),
        tenor: isApprovedLoan ? (parseInt(Loan.tenor || tenorMonths, 10) || null) : null
        // do NOT send createdAt/createdBy from client — backend should set them
      };

      let res;
      if (isEdit) {
        res = await LoanDataService.update(id, payload);
      } else {
        res = await LoanDataService.create(Loan.owner, payload);
      }
      const r = res?.data || {};
      const ownerId = (r.owner && typeof r.owner === "object") ? (r.owner._id || r.owner.id || String(r.owner)) : (r.owner ?? Loan.owner);

      // Record Stamp Duty or waiver for Loan Repayment deposit
      if (Loan.creditOrDebit === "debit" && !isEdit) {
        const dutyNum = applyStampDuty ? (parseNum(stampDutyAmount) || 50) : 0;
        StampDutyService.record({
          memberId: ownerId,
          depositAmount: payload.amount,
          stampDutyAmount: dutyNum,
          accountType: "loan",
          isWaived: !applyStampDuty,
          waiveReason: !applyStampDuty ? (waiveReason || "Waived on Loan Repayment deposit") : "",
          date: payload.date,
          notes: payload.description || `Loan Repayment deposit (Stamp Duty: ₦${dutyNum})`
        }).catch(err => console.error("Stamp duty recording error:", err));
      }

      // update local state with server-returned metadata (created/updated/newBalance)
      setLoan({
        owner: ownerId,
        date: r.date ?? payload.date,
        amount: r.amount ?? payload.amount,
        creditOrDebit: r.creditOrDebit ?? payload.creditOrDebit,
        description: r.description ?? payload.description,
        tenor: r.tenor ?? payload.tenor,
        createdAt: r.createdAt ?? null,
        createdBy: r.createdBy ?? null,
        updatedAt: r.updatedAt ?? null,
        updatedBy: r.updatedBy ?? null,
        newBalance: r.newBalance ?? null
      });
      // update displayed owner name when server returns owner as object
      if (r.owner && typeof r.owner === "object") {
        const fname = r.owner.firstName || r.owner.firstname || r.owner.first_name || r.owner.name || "";
        const lname = r.owner.lastName || r.owner.lastname || r.owner.last_name || "";
        setOwnerDisplay((lname ? lname + (fname ? " " : "") : "") + fname);
      }

      setSubmitted(true);
      setSnackOpen(true);

      navigate(`/dashboard/loan/${encodeURIComponent(ownerId)}`);
    } catch (e) {
      console.error(e);
      setError(e?.response?.data?.message || e?.message || "Save failed");
    } finally {
      setLoading(false);
    }
  };

  const newLoan = () => {
    setLoan({ ...initialLoanState, owner: owner || initialLoanState.owner });
    setTenorMonths("");
    setSubmitted(false);
    setError("");
    setOwnerDisplay("");
  };

  return (
    <Paper elevation={3} sx={{ maxWidth: 840, margin: "24px auto", p: { xs: 2, md: 4 } }}>
      <Typography variant="h6" gutterBottom>
        {submitted ? "Transaction saved" : "Add Loan Transaction"}
      </Typography>

      {error && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      )}

      {submitted ? (
        <Box sx={{ mt: 2 }}>
          <Stack direction="row" spacing={2}>
            <Button variant="contained" color="primary" onClick={newLoan}>
              Add Another
            </Button>
          </Stack>
        </Box>
      ) : (
        <Box component="form" noValidate autoComplete="off" sx={{ mt: 1 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                id="owner"
                name="owner"
                label="Owner"
                value={ownerDisplay || ""}
                fullWidth
                InputProps={{ readOnly: true }}
                helperText={owner ? "Owner provided via route (read-only)" : "Owner (read-only)"}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                id="date"
                name="date"
                label="Date of Deposit"
                helperText="Date deposit occurred (system creation timestamp is recorded automatically)"
                type="date"
                value={Loan.date}
                onChange={handleInputChange}
                fullWidth
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                id="amount"
                name="amount"
                label="Amount"
                type="text"
                placeholder="e.g. 4,000,000.00"
                value={Loan.amount}
                onChange={(e) => {
                  const val = formatInputWithCommas(e.target.value);
                  setLoan(prev => ({ ...prev, amount: val }));
                }}
                onBlur={() => {
                  if (Loan.amount) {
                    setLoan(prev => ({ ...prev, amount: formatToCurrencyString(Loan.amount) }));
                  }
                }}
                fullWidth
                InputProps={{ inputMode: "numeric" }}
                required
              />
              <Button
                startIcon={<CalculateRoundedIcon />}
                size="small"
                sx={{ mt: 1 }}
                onClick={() => setShowCalc(true)}
              >
                Calculator
              </Button>
              <Dialog open={showCalc} keepMounted onClose={() => setShowCalc(false)} maxWidth="xs" fullWidth>
                <DialogContent sx={{ p: 0 }}>
                  <SmallCalculator
                    initialAmount={Loan.amount}
                    onApplyLoan={(val) => {
                      const v = (val == null) ? "" : formatToCurrencyString(val);
                      setLoan(prev => ({ ...prev, amount: v }));
                      setShowCalc(false);
                    }}
                    onClose={() => setShowCalc(false)}
                  />
                </DialogContent>
              </Dialog>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 600, color: "#334155" }}>
                Transaction Type
              </Typography>
              <Stack direction="row" spacing={2} alignItems="center">
                <Button
                  variant={Loan.creditOrDebit === "credit" ? "contained" : "outlined"}
                  color="primary"
                  onClick={() => {
                    // open tenor dialog when selecting Approved Loan
                    setTenorMonths(Loan.tenor || "6");
                    setTenorError("");
                    setGuarantor1("");
                    setGuarantor2("");
                    setGuarantorError("");
                    setTenorDialogOpen(true);
                  }}
                  sx={{ minWidth: 150, textTransform: "none", fontWeight: 700 }}
                >
                  Approved Loan (+)
                </Button>
                <Button
                  variant={Loan.creditOrDebit === "debit" ? "contained" : "outlined"}
                  color="secondary"
                  onClick={() => {
                    setLoan(s => ({ ...s, creditOrDebit: "debit", tenor: "" }));
                    setTenorMonths("");
                  }}
                  sx={{ minWidth: 150, textTransform: "none", fontWeight: 700 }}
                >
                  Loan Repayment (-)
                </Button>
              </Stack>
              <Typography variant="caption" sx={{ color: "#64748B", display: "block", mt: 0.5 }}>
                {Loan.creditOrDebit === "credit"
                  ? "Approved Loan increases the total loan debt/amount for this member."
                  : Loan.creditOrDebit === "debit"
                  ? "Loan Repayment decreases the total loan balance (debit) for this member."
                  : "Select Approved Loan to disburse or Loan Repayment to reduce loan balance."}
              </Typography>
            </Grid>

            {Loan.creditOrDebit === "credit" && (
              <Grid item xs={12} md={6}>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 700, color: "#0B3B60" }}>
                  Repayment Tenor (Months) *
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center" }}>
                  {[3, 6, 9, 12].map((m) => (
                    <Button
                      key={m}
                      size="small"
                      variant={String(Loan.tenor || tenorMonths) === String(m) ? "contained" : "outlined"}
                      onClick={() => {
                        setLoan(s => ({ ...s, tenor: String(m) }));
                        setTenorMonths(String(m));
                      }}
                      sx={{
                        minWidth: 82,
                        textTransform: "none",
                        fontWeight: 700,
                        backgroundColor: String(Loan.tenor || tenorMonths) === String(m) ? "#D97706" : "#ffffff",
                        borderColor: "#D97706",
                        color: String(Loan.tenor || tenorMonths) === String(m) ? "#ffffff" : "#D97706",
                        boxShadow: String(Loan.tenor || tenorMonths) === String(m) ? "0 2px 6px rgba(217,119,6,0.25)" : "none",
                        "&:hover": {
                          backgroundColor: String(Loan.tenor || tenorMonths) === String(m) ? "#B45309" : "rgba(217, 119, 6, 0.08)",
                          borderColor: "#B45309"
                        }
                      }}
                    >
                      {m} Months
                    </Button>
                  ))}
                  <TextField
                    id="tenor"
                    name="tenor"
                    label="Custom Months"
                    placeholder="e.g. 6"
                    type="number"
                    size="small"
                    value={Loan.tenor || tenorMonths || ""}
                    onChange={(e) => {
                      handleInputChange(e);
                      setTenorMonths(e.target.value);
                    }}
                    inputProps={{ min: 1, max: 60 }}
                    sx={{ width: 140 }}
                    helperText="Or type custom number"
                  />
                </Box>
              </Grid>
            )}

            {/* PRE-APPROVAL APPLICATION FORM FEE VERIFICATION */}
            {isApprovedLoan && (
              <Grid item xs={12}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    borderRadius: 2.5,
                    border: "1px solid",
                    borderColor: memberFeeInfo?.hasPaid ? "#86EFAC" : "#FDE68A",
                    backgroundColor: memberFeeInfo?.hasPaid ? "#F0FDF4" : "#FFFBEB",
                    display: "flex",
                    flexWrap: "wrap",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 1.5
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    {memberFeeInfo?.hasPaid ? (
                      <VerifiedUserRoundedIcon sx={{ color: "#16A34A", fontSize: 28 }} />
                    ) : (
                      <PostAddRoundedIcon sx={{ color: "#D97706", fontSize: 28 }} />
                    )}
                    <Box>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 800, color: memberFeeInfo?.hasPaid ? "#166534" : "#92400E" }}>
                          Loan Application Form Fee:
                        </Typography>
                        {loadingFee ? (
                          <CircularProgress size={16} sx={{ color: "#D97706" }} />
                        ) : memberFeeInfo?.hasPaid ? (
                          <Chip
                            label={`PAID: ₦${fNumber(memberFeeInfo.totalPaid)}`}
                            size="small"
                            sx={{ fontWeight: 800, backgroundColor: "#DCFCE7", color: "#15803D" }}
                          />
                        ) : (
                          <Chip
                            label="NOT YET RECORDED"
                            size="small"
                            sx={{ fontWeight: 800, backgroundColor: "#FEF3C7", color: "#B45309" }}
                          />
                        )}
                      </Box>
                      <Typography variant="caption" sx={{ color: memberFeeInfo?.hasPaid ? "#15803D" : "#78350F" }}>
                        {memberFeeInfo?.hasPaid
                          ? `Receipt Ref: ${memberFeeInfo.latest?.receiptNo || 'Verified'} • Paid on ${memberFeeInfo.latest?.paymentDate ? new Date(memberFeeInfo.latest.paymentDate).toLocaleDateString() : 'N/A'}`
                          : "Members must pay their loan application form fee before loan approval. Financial Secretary can record it now."}
                      </Typography>
                    </Box>
                  </Box>

                  <Button
                    size="small"
                    variant={memberFeeInfo?.hasPaid ? "outlined" : "contained"}
                    onClick={() => setAppFeeModalOpen(true)}
                    startIcon={<PostAddRoundedIcon />}
                    sx={{
                      textTransform: "none",
                      fontWeight: 700,
                      borderRadius: "8px",
                      backgroundColor: memberFeeInfo?.hasPaid ? "#ffffff" : "#D97706",
                      color: memberFeeInfo?.hasPaid ? "#166534" : "#ffffff",
                      borderColor: memberFeeInfo?.hasPaid ? "#86EFAC" : "transparent",
                      "&:hover": {
                        backgroundColor: memberFeeInfo?.hasPaid ? "#ECFDF5" : "#B45309"
                      }
                    }}
                  >
                    {memberFeeInfo?.hasPaid ? "View Form Fee Ledger" : "Record Application Fee Now"}
                  </Button>
                </Paper>
              </Grid>
            )}

            {/* AUTOMATIC COT (COMMISSION ON TURNOVER) CHARGES PANEL */}
            {isApprovedLoan && (
              <Grid item xs={12}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2.5,
                    borderRadius: 2.5,
                    border: "1px solid #CBD5E1",
                    background: "linear-gradient(135deg, #F8FAFC 0%, #EFF6FF 100%)"
                  }}
                >
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 1, mb: 1.5 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <CalculateRoundedIcon sx={{ color: "#0B3B60" }} />
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#0B3B60" }}>
                          Auto COT Charges (Commission on Turnover)
                        </Typography>
                        <Typography variant="caption" sx={{ color: "#64748B" }}>
                          Automatically applied administrative commission added to approved loan debt
                        </Typography>
                      </Box>
                    </Box>

                    <FormControlLabel
                      control={
                        <Switch
                          checked={applyCot}
                          onChange={(e) => setApplyCot(e.target.checked)}
                          color="primary"
                        />
                      }
                      label={applyCot ? `Apply ${effectiveCotRate}% COT Charge` : "Waive COT Charge"}
                      sx={{ "& .MuiTypography-root": { fontWeight: 700, fontSize: "0.85rem", color: applyCot ? "#0B3B60" : "#64748B" } }}
                    />
                  </Box>

                  {applyCot && (
                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1, mb: 1.5 }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: "#475569" }}>
                          COT Rate Presets:
                        </Typography>
                        {[
                          { r: 0.1, label: "0.1% (Standard)" },
                          { r: 0.25, label: "0.25%" },
                          { r: 0.5, label: "0.5%" },
                          { r: 1.0, label: "1.0%" }
                        ].map(({ r, label }) => (
                          <Button
                            key={r}
                            size="small"
                            variant={Number(cotRate) === r && customCotRate === "" ? "contained" : "outlined"}
                            onClick={() => {
                              setCotRate(r);
                              setCustomCotRate("");
                            }}
                            sx={{
                              fontSize: "0.75rem",
                              fontWeight: 700,
                              textTransform: "none",
                              borderRadius: "8px",
                              backgroundColor: Number(cotRate) === r && customCotRate === "" ? "#0B3B60" : "#ffffff",
                              borderColor: Number(cotRate) === r && customCotRate === "" ? "#0B3B60" : "#CBD5E1",
                              color: Number(cotRate) === r && customCotRate === "" ? "#ffffff" : "#334155"
                            }}
                          >
                            {label}
                          </Button>
                        ))}

                        <TextField
                          size="small"
                          label="Custom COT %"
                          placeholder="e.g. 1.2"
                          type="number"
                          value={customCotRate}
                          onChange={(e) => setCustomCotRate(e.target.value)}
                          InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                          sx={{ width: 140, ml: 1, backgroundColor: "#ffffff" }}
                        />
                      </Box>
                    </Box>
                  )}

                  {/* 4 Summary Live Metrics */}
                  <Grid container spacing={1.5}>
                    <Grid item xs={6} sm={3}>
                      <Box sx={{ p: 1.5, borderRadius: 2, backgroundColor: "#ffffff", border: "1px solid #E2E8F0" }}>
                        <Typography variant="caption" sx={{ color: "#64748B", fontWeight: 700 }}>
                          PRINCIPAL LOAN
                        </Typography>
                        <Typography variant="subtitle1" sx={{ fontWeight: 800, color: "#0B3B60" }}>
                          ₦{fNumber(principalNum)}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Box sx={{ p: 1.5, borderRadius: 2, backgroundColor: "#ffffff", border: "1px solid #E2E8F0" }}>
                        <Typography variant="caption" sx={{ color: "#64748B", fontWeight: 700 }}>
                          AUTO COT ({effectiveCotRate}%)
                        </Typography>
                        <Typography variant="subtitle1" sx={{ fontWeight: 800, color: applyCot ? "#D97706" : "#94A3B8" }}>
                          +₦{fNumber(cotAmount)}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Box sx={{ p: 1.5, borderRadius: 2, backgroundColor: "#F0FDF4", border: "1px solid #86EFAC" }}>
                        <Typography variant="caption" sx={{ color: "#166534", fontWeight: 700 }}>
                          TOTAL DEBT BILLED
                        </Typography>
                        <Typography variant="subtitle1" sx={{ fontWeight: 800, color: "#15803D" }}>
                          ₦{fNumber(totalLoanWithCot)}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Box sx={{ p: 1.5, borderRadius: 2, backgroundColor: "#ffffff", border: "1px solid #E2E8F0" }}>
                        <Typography variant="caption" sx={{ color: "#64748B", fontWeight: 700 }}>
                          EST. MONTHLY ({tenorMonthsNum}m)
                        </Typography>
                        <Typography variant="subtitle1" sx={{ fontWeight: 800, color: "#2563EB" }}>
                          ₦{fNumber(monthlyInstallment)}/mo
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
            )}

            {/* STAMP DUTY CONTROL FOR LOAN REPAYMENT DEPOSIT */}
            {Loan.creditOrDebit === "debit" && (
              <Grid item xs={12}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: "#F8FAFC",
                    border: "1px solid #E2E8F0"
                  }}
                >
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <VerifiedUserRoundedIcon sx={{ color: applyStampDuty ? "#0D9488" : "#94A3B8" }} />
                      <Typography variant="body2" sx={{ fontWeight: 700, color: "#1E293B" }}>
                        Stamp Duty (EMTL)
                      </Typography>
                    </Box>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={applyStampDuty}
                          onChange={(e) => setApplyStampDuty(e.target.checked)}
                          color="primary"
                        />
                      }
                      label={applyStampDuty ? `Apply ₦${stampDutyAmount || 50} Stamp Duty` : "Waive Stamp Duty"}
                      sx={{ "& .MuiTypography-root": { fontWeight: 700, fontSize: "0.85rem" } }}
                    />
                  </Box>

                  {applyStampDuty ? (
                    <Box sx={{ mt: 1.5, display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: "#475569" }}>
                        Duty Amount:
                      </Typography>
                      {[50, 100, 200].map((preset) => (
                        <Button
                          key={preset}
                          size="small"
                          variant={String(stampDutyAmount) === String(preset) ? "contained" : "outlined"}
                          onClick={() => setStampDutyAmount(String(preset))}
                          sx={{
                            minWidth: 64,
                            py: 0.3,
                            px: 1,
                            fontSize: "0.75rem",
                            fontWeight: 700,
                            borderRadius: "6px",
                            bgcolor: String(stampDutyAmount) === String(preset) ? "#0D9488" : "transparent",
                            color: String(stampDutyAmount) === String(preset) ? "#ffffff" : "#0D9488",
                            borderColor: "#0D9488"
                          }}
                        >
                          ₦{preset} {preset === 50 ? "(Std)" : ""}
                        </Button>
                      ))}
                      <TextField
                        size="small"
                        type="number"
                        label="Custom Duty (₦)"
                        value={stampDutyAmount}
                        onChange={(e) => setStampDutyAmount(e.target.value)}
                        inputProps={{ min: 0 }}
                        sx={{ width: 140, backgroundColor: "#ffffff" }}
                      />
                    </Box>
                  ) : (
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Reason for waiving (e.g. Single bulk transfer split across savings/shares/loan)"
                      value={waiveReason}
                      onChange={(e) => setWaiveReason(e.target.value)}
                      sx={{ mt: 1.5, backgroundColor: "#ffffff" }}
                    />
                  )}

                  <Box sx={{ mt: 1.5, pt: 1, borderTop: "1px dashed #E2E8F0", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
                    <Typography variant="caption" sx={{ color: "#64748B" }}>
                      Is this part of a single bank deposit shared between Savings, Shares, and Loan?
                    </Typography>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => setSplitModalOpen(true)}
                      startIcon={<CallSplitRoundedIcon />}
                      sx={{ textTransform: "none", fontWeight: 700, borderRadius: "6px", fontSize: "0.75rem", borderColor: "#0D9488", color: "#0D9488" }}
                    >
                      Single Deposit Split Tool (1x Stamp Duty)
                    </Button>
                  </Box>
                </Paper>
              </Grid>
            )}

            <Grid item xs={12}>
              <TextField
                id="description"
                name="description"
                label="Description (optional)"
                value={Loan.description}
                onChange={handleInputChange}
                fullWidth
                multiline
                rows={2}
                placeholder="Enter a brief description of the transaction"
              />
            </Grid>

            <Grid item xs={12}>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <Button
                  variant="contained"
                  color="success"
                  onClick={saveLoan}
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={18} /> : null}
                >
                  Submit
                </Button>
                <Button variant="outlined" onClick={newLoan} disabled={loading}>
                  Reset
                </Button>
                <Button variant="text" onClick={() => navigate(-1)}>
                  Cancel
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </Box>
      )}

      <Snackbar
        open={snackOpen}
        autoHideDuration={3000}
        onClose={() => setSnackOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={() => setSnackOpen(false)} severity="success" sx={{ width: "100%" }}>
          Transaction saved successfully
        </Alert>
      </Snackbar>

      {/* Tenor dialog: ask months when credit selected */}
      <Dialog open={tenorDialogOpen} onClose={() => setTenorDialogOpen(false)}>
        <DialogTitle>Tenor — How many months to repay?</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", gap: 1, mb: 2, mt: 1 }}>
            {[3, 6, 9, 12].map((m) => (
              <Button
                key={m}
                size="small"
                variant={String(tenorMonths) === String(m) ? "contained" : "outlined"}
                onClick={() => setTenorMonths(String(m))}
                sx={{
                  fontWeight: 700,
                  backgroundColor: String(tenorMonths) === String(m) ? "#D97706" : "#ffffff",
                  borderColor: "#D97706",
                  color: String(tenorMonths) === String(m) ? "#ffffff" : "#D97706",
                  "&:hover": {
                    backgroundColor: String(tenorMonths) === String(m) ? "#B45309" : "rgba(217, 119, 6, 0.08)"
                  }
                }}
              >
                {m} Mos
              </Button>
            ))}
          </Box>
          <TextField
            autoFocus
            margin="dense"
            label="Months"
            type="number"
            fullWidth
            value={tenorMonths}
            onChange={(e) => setTenorMonths(e.target.value)}
            helperText={tenorError || "Enter number of months (e.g. 6)"}
            error={Boolean(tenorError)}
            inputProps={{ min: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTenorDialogOpen(false)}>Cancel</Button>
          <Button onClick={() => {
            const n = Number(tenorMonths);
            if (!Number.isInteger(n) || n <= 0) {
              setTenorError("Please enter a valid number of months");
              return;
            }
            // open guarantor dialog next; do not modify description yet
            setTenorError("");
            setTenorDialogOpen(false);
            setLoan(s => ({ ...s, tenor: String(n) }));
            setGuarantor1("");
            setGuarantor2("");
            setGuarantorError("");
            setGuarantorDialogOpen(true);
          }}>Confirm</Button>
        </DialogActions>
      </Dialog>

      {/* Guarantor dialog: ask two guarantator names and append both names to description along with tenor */}
      <Dialog open={guarantorDialogOpen} onClose={() => setGuarantorDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Guarantors — Enter Names</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            <Autocomplete
              freeSolo
              options={g1Options}
              loading={loadingGuarantors}
              value={guarantor1}
              onChange={(event, newValue) => {
                setGuarantor1(typeof newValue === "string" ? newValue : (newValue?.label || newValue || ""));
              }}
              onInputChange={(event, newInputValue) => {
                setGuarantor1(newInputValue);
              }}
              filterOptions={(options, state) => {
                const query = (state.inputValue || "").toLowerCase().trim();
                if (!query) return options;
                return options.filter(opt => opt.toLowerCase().includes(query));
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  autoFocus
                  margin="dense"
                  label="Guarantor 1 Name"
                  placeholder="Type member name (list reduces to match)..."
                  helperText="Enter first guarantor full name"
                  fullWidth
                />
              )}
            />

            <Autocomplete
              freeSolo
              options={g2Options}
              loading={loadingGuarantors}
              value={guarantor2}
              onChange={(event, newValue) => {
                setGuarantor2(typeof newValue === "string" ? newValue : (newValue?.label || newValue || ""));
              }}
              onInputChange={(event, newInputValue) => {
                setGuarantor2(newInputValue);
              }}
              filterOptions={(options, state) => {
                const query = (state.inputValue || "").toLowerCase().trim();
                if (!query) return options;
                return options.filter(opt => opt.toLowerCase().includes(query));
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  margin="dense"
                  label="Guarantor 2 Name (optional)"
                  placeholder="Type member name (list reduces to match)..."
                  helperText="Enter second guarantor full name"
                  fullWidth
                  sx={{ mt: 1.5 }}
                />
              )}
            />
          </Box>
          {guarantorError && <Box sx={{ mt: 1.5 }}><Alert severity="error">{guarantorError}</Alert></Box>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGuarantorDialogOpen(false)}>Cancel</Button>
          <Button onClick={() => {
            // require at least one guarantor name
            if (!guarantor1.trim() && !guarantor2.trim()) {
              setGuarantorError("Please enter at least one guarantor name");
              return;
            }

            // Borrower cannot be guarantor
            if (ownerDisplay) {
              const bNorm = ownerDisplay.trim().toLowerCase();
              const g1Norm = (guarantor1 || "").trim().toLowerCase();
              const g2Norm = (guarantor2 || "").trim().toLowerCase();
              const isMatchG1 = g1Norm && (g1Norm === bNorm || g1Norm.startsWith(bNorm) || bNorm.startsWith(g1Norm));
              const isMatchG2 = g2Norm && (g2Norm === bNorm || g2Norm.startsWith(bNorm) || bNorm.startsWith(g2Norm));
              if (isMatchG1 || isMatchG2) {
                setGuarantorError(`The borrowing member (${ownerDisplay}) cannot serve as their own guarantor.`);
                return;
              }
            }

            // Guarantor 1 and Guarantor 2 must not be the same name
            if (guarantor1.trim() && guarantor2.trim()) {
              const g1Clean = cleanGuarantorName(guarantor1);
              const g2Clean = cleanGuarantorName(guarantor2);
              if (g1Clean && g2Clean && g1Clean === g2Clean) {
                setGuarantorError("Guarantor 1 and Guarantor 2 cannot be the same person. Please enter two different guarantors.");
                return;
              }
            }

            setGuarantorError("");
            const n = Number(tenorMonths);
            const tenorText = (Number.isInteger(n) && n > 0) ? `Tenor: ${n} month${n > 1 ? "s" : ""}` : "";
            const guarantorText = `Guarantors: ${guarantor1.trim()}${guarantor2.trim() ? `; ${guarantor2.trim()}` : ""}`;
            setLoan(prev => {
              const desc = (prev.description || "").trim();
              const parts = [];
              if (desc) parts.push(desc);
              if (tenorText) parts.push(tenorText);
              parts.push(guarantorText);
              const newDesc = parts.join(" — ");
              return { ...prev, creditOrDebit: "credit", tenor: String(n), description: newDesc };
            });
            setGuarantorDialogOpen(false);
          }}>Confirm</Button>
        </DialogActions>
      </Dialog>
      {/* Loan Application Fee Modal for Financial Secretary */}
      <LoanApplicationFeeModal
        open={appFeeModalOpen}
        onClose={() => setAppFeeModalOpen(false)}
        initialMemberId={Loan.owner || owner}
        onSuccess={() => fetchMemberFee(Loan.owner || owner)}
      />

      {/* SINGLE DEPOSIT SPLIT MODAL */}
      <SplitDepositModal
        open={splitModalOpen}
        onClose={() => setSplitModalOpen(false)}
        initialMemberId={Loan.owner || owner}
        onSuccess={() => {
          setSnackOpen(true);
          const ownerId = Loan.owner || owner;
          if (ownerId) {
            navigate(`/dashboard/loan/${encodeURIComponent(ownerId)}`);
          }
        }}
      />
    </Paper>
  );
};

export default AddLoan;
